import { privateBaseQuery } from '@/api/base/private/privateBaseQuery';
import { createApi } from '@reduxjs/toolkit/query/react';

import {
  GetGroupsAsManagerResponse as GetGroupsAsManager,
  GetGroupsAsManagerParams,
  GroupsEntities as Entities,
  GetClassAsManagerResponse as GetClassAsManager,
  ManagerClassEntity,
  CreateClassAsManagerResponse as CreateClassAsManager,
  CreateClassRequest,
  CreateClassAsManagerEntity,
  UpdateClassAsManagerResponse as UpdateClassAsManager,
  UpdateClassRequest,
  UpdateClassAsManagerEntity,
  CreateStudentsFromFilePayload,
  CreateStudentsFromFileResponse,
  GetUsersByClassRequest,
  GetUsersByClassResponse,
  GetClassesResponse,
  GetStudentsInAssignmentAsManagerParams,
  GetStudentsInAssignmentAsManagerResponse,
  StudentInAssignmentEntity,
  GetStudentsInAssignmentForProctoringEntities,
  GetStudentsInAssignmentForProctoringParams,
  GetStudentsInAssignmentForProctoringResponse,
  GetStudentsInClassAsManagerParams,
  GetStudentsInClassAsManagerResponse,
  GetStudentsAsManagerParams,
  GetStudentsAsManagerResponse,
} from '@/types/canonical';
import {
  GetStudentsInAssignmentAsManagerRequestedFields,
  GetStudentsInAssignmentForProctoringFields,
  ManagerClassRequestedModelFields,
  StudentClassRequestedModelFields,
  GetStudentsAsManagerFullInfoRequestedFields,
} from './classFields';

export interface GetClassDetailParams {
  classId: number;
  role?: string;
}

export type BulkCreateStudentUser = {
  id: number;
  firstname: string;
  lastname: string;
  photo: string | null;
};

export type BulkCreatedStudentEntity = {
  index: number;
  id: number;
  user_id: number;
  user: BulkCreateStudentUser;
};

export type BulkCreateStudentsResponse = {
  status: string;
  status_code: string;
  message: string;
  created_count: number;
  entities: BulkCreatedStudentEntity[];
};

export interface DetachUserFromClassResponse {
  status: string;
  status_code: string;
  message: string;
  entity: {
    class_id: number;
    role: string;
    student_id: number;
    user_id: number;
    detached_from_assignments_count?: number;
  };
}

export const classApi = createApi({
  reducerPath: 'classApi',
  baseQuery: privateBaseQuery(),
  tagTypes: ['ClassDetail', 'ClassesList', 'ClassMembers', 'Students'],

  endpoints: (builder) => ({
    getClassesAsManager: builder.query<Entities, GetGroupsAsManagerParams>({
      query: (params) => ({
        url: '/classes',
        method: 'GET',
        params,
        headers: {
          'X-Requested-Fields': StudentClassRequestedModelFields.join(','),
        },
      }),
      transformResponse: (response: GetGroupsAsManager) => response.entities,
      keepUnusedDataFor: 600,
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const safeQuery = queryArgs?.query?.toString?.().trim?.() ?? "";
        const safeOrder = queryArgs?.orderBy ?? "";
        const safeLimit = queryArgs?.limit ?? 10;
        return `${endpointName}|${safeQuery}|${safeOrder}|${safeLimit}`;
      },
      merge: (currentCache, newData, { arg }) => {
        // Page 1 — это «новый старт» (фильтры/удаление/инвалидация). Заменяем кеш целиком,
        // иначе удалённые сервером элементы будут жить в кеше из предыдущих страниц.
        if (!currentCache?.data || arg?.page === 1) {
          return Object.assign(currentCache, newData);
        }

        const existing = currentCache.data ?? [];
        const incoming = newData?.data ?? [];

        const byId = new Map<number, any>();
        for (const item of existing as any[]) {
          if (item?.id != null) byId.set(item.id, item);
        }
        for (const item of incoming as any[]) {
          if (item?.id != null) byId.set(item.id, item);
        }

        Object.assign(currentCache, newData, {
          data: Array.from(byId.values()),
        });
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const nextQuery = currentArg?.query?.toString?.().trim?.() ?? "";
        const prevQuery = previousArg?.query?.toString?.().trim?.() ?? "";
        const nextOrder = currentArg?.orderBy ?? "";
        const prevOrder = previousArg?.orderBy ?? "";
        const nextLimit = currentArg?.limit ?? 10;
        const prevLimit = previousArg?.limit ?? 10;
        const nextPage = currentArg?.page ?? 1;
        const prevPage = previousArg?.page ?? 1;
        return (
          nextQuery !== prevQuery ||
          nextOrder !== prevOrder ||
          nextLimit !== prevLimit ||
          nextPage !== prevPage
        );
      },
      providesTags: ['ClassesList'],
    }),
    getClassAsManager: builder.query<ManagerClassEntity, GetClassDetailParams>({
      query: ({ classId }) => ({
        url: `/classes/${classId}`,
        method: 'GET',
        headers: {
          'X-Requested-Fields': ManagerClassRequestedModelFields.join(','),
        },
      }),
      transformResponse: (response: GetClassAsManager) => response.entity,
      providesTags: (result, error, { classId }) => [
        { type: 'ClassDetail', id: classId },
      ],
    }),
    updateClass: builder.mutation<UpdateClassAsManagerEntity, UpdateClassRequest>({
      query: (payload) => ({
        url: `/classes/${payload.id}`,
        method: 'PUT',
        data: payload,
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: (response: UpdateClassAsManager) => response.entity,
      invalidatesTags: (result, error, { id }) => [
        { type: 'ClassDetail', id },
        'ClassesList',
        { type: 'ClassMembers', id: `${id}:students` },
        { type: 'ClassMembers', id: `${id}:teachers` },
      ],
    }),

    // POST /classes (create group/class)
    createClass: builder.mutation<CreateClassAsManagerEntity, CreateClassRequest>({
      query: (payload) => ({
        url: '/classes',
        method: 'POST',
        data: payload,
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: (response: CreateClassAsManager) => response.entity,
      invalidatesTags: ['ClassesList'],
    }),
    createBulkInClass: builder.mutation<
      CreateStudentsFromFileResponse,
      { classId: number; payload: CreateStudentsFromFilePayload }
    >({
      query: ({ classId, payload }) => ({
        url: `/classes/bulk`,
        method: 'POST',
        data: { class_id: classId, ...payload },
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: (r, e, { classId }) => [
        { type: 'ClassDetail', id: classId },
        { type: 'ClassMembers', id: `${classId}:students` },
      ],
    }),

    // POST /students/bulk (bulk create students)
    createBulkStudents: builder.mutation<BulkCreateStudentsResponse, Record<string, unknown>[]>({
      query: (students) => ({
        url: `/students/bulk`,
        method: 'POST',
        data: students,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: [{ type: 'Students', id: 'LIST' }],
    }),

    /**
     * Mock endpoint: save imported students for a class/group.
     * NOTE: temporary stub until backend contract is finalized.
     */
    saveStudentsImportMock: builder.mutation<
      CreateStudentsFromFileResponse,
      {
        classId: number;
        payload: {
          parsedData: string[][];
          mappingSelection: Record<string, number>;
          startRow: number;
          updateExisting: boolean;
          emailDomain: string;
          defaultPassword: string;
          groupTemplate: string;
        };
      }
    >({
      async queryFn({ payload }) {
        // Minimal fake response: return rows count as created_count.
        const created_count = Array.isArray(payload.parsedData) ? payload.parsedData.length : 0;

        return {
          data: {
            status: "success",
            status_code: "200",
            message: "Mock import saved",
            created_count,
            entities: [],
          },
        };
      },
      invalidatesTags: (_r, _e, { classId }) => [
        { type: "ClassDetail", id: classId },
        { type: "ClassMembers", id: `${classId}:students` },
      ],
    }),
    getStudentsInAssignmentAsManager: builder.query<
      GetStudentsInAssignmentAsManagerResponse,
      GetStudentsInAssignmentAsManagerParams
    >({
      query: ({
        page = 1,
        limit = 10,
        assignment_id,
        sortBy = "lastname",
        query: searchQuery,
      }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          assignment_id: assignment_id.toString(),
          sortBy,
        });

        const q = searchQuery?.toString?.().trim?.() ?? "";
        if (q) params.append("query", q);

        return {
          url: `/students?${params.toString()}`,
          method: "GET",
          headers: {
            "X-Requested-Fields":
              GetStudentsInAssignmentAsManagerRequestedFields.join(","),
          },
        };
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const safeAssignmentId = queryArgs?.assignment_id ?? 0;
        const safeSort = queryArgs?.sortBy ?? "";
        const safeQuery = queryArgs?.query?.toString?.().trim?.() ?? "";
        const safePage = queryArgs?.page ?? 1;
        const safeLimit = queryArgs?.limit ?? 10;
        return `${endpointName}|${safeAssignmentId}|${safeSort}|${safeQuery}|${safePage}|${safeLimit}`;
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const nextAssignmentId = currentArg?.assignment_id ?? 0;
        const prevAssignmentId = previousArg?.assignment_id ?? 0;
        const nextSort = currentArg?.sortBy ?? "";
        const prevSort = previousArg?.sortBy ?? "";
        const nextQuery = currentArg?.query?.toString?.().trim?.() ?? "";
        const prevQuery = previousArg?.query?.toString?.().trim?.() ?? "";
        const nextPage = currentArg?.page ?? 1;
        const prevPage = previousArg?.page ?? 1;
        const nextLimit = currentArg?.limit ?? 10;
        const prevLimit = previousArg?.limit ?? 10;
        return (
          nextAssignmentId !== prevAssignmentId ||
          nextSort !== prevSort ||
          nextQuery !== prevQuery ||
          nextPage !== prevPage ||
          nextLimit !== prevLimit
        );
      },
      providesTags: (_r, _e, arg) => [
        { type: "Students", id: `assignment:${arg.assignment_id}` },
      ],
    }),

    // Get external students (added individually, not from class group) for an assignment
    getExternalStudentsInAssignment: builder.query<
      GetStudentsInAssignmentAsManagerResponse,
      { assignment_id: number; page?: number; limit?: number; sortBy?: string }
    >({
      query: ({
        assignment_id,
        page = 1,
        limit = 100,
        sortBy = "lastname",
      }) => ({
        url: `/students?page=${page}&limit=${limit}&assignment_id=${assignment_id}&students_filter=external&sortBy=${sortBy}`,
        method: "GET",
        headers: {
          "X-Requested-Fields":
            GetStudentsAsManagerFullInfoRequestedFields.join(","),
        },
      }),
      providesTags: (_r, _e, arg) => [{ type: "Students", id: `external:${arg.assignment_id}` }],
    }),
    getStudentInAssignmentAsManager: builder.query<
      StudentInAssignmentEntity,
      { studentId: number; assignmentId: number; sortBy?: string }
    >({
      query: ({ studentId, assignmentId, sortBy = "lastname" }) => ({
        url: `/students/${studentId}`,
        method: "GET",
        params: {
          assignment_id: assignmentId,
          sortBy,
        },
        headers: {
          "X-Requested-Fields":
            GetStudentsInAssignmentAsManagerRequestedFields.join(","),
        },
      }),
      transformResponse: (response: {
        status: string;
        status_code: string;
        entity: StudentInAssignmentEntity;
      }) => response.entity,
    }),

    getStudentsInAssignmentForProctoring: builder.query<
      GetStudentsInAssignmentForProctoringEntities,
      GetStudentsInAssignmentForProctoringParams
    >({
      query: ({ page = 1, limit = 10, assignment_id, webinar_id, id, query: searchQuery }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          assignment_id: assignment_id.toString(),
        });
        
        if (webinar_id !== undefined) {
          params.append('webinar_id', webinar_id.toString());
        }
        
        // Добавляем поисковый запрос
        if (searchQuery && searchQuery.trim()) {
          params.append('query', searchQuery.trim());
        }
        
        // Добавляем массив ID для фильтрации (для фильтра "в сети")
        if (id && id.length > 0) {
          id.forEach(studentId => params.append('id[]', studentId.toString()));
        }
        
        return {
          url: `/students?${params.toString()}`,
          method: 'GET',
          headers: {
            'X-Requested-Fields': GetStudentsInAssignmentForProctoringFields.join(','),
          },
        };
      },
      transformResponse: (v: GetStudentsInAssignmentForProctoringResponse) => v.entities,
    }),
    getTasksInClassAsManager: builder.query<
      UpdateClassAsManager,
      { classId: number }
    >({
      query: ({ classId }) => ({
        url: `classes/${classId}/tasks`,
        method: 'GET',
      }),
    }),
    getClasses: builder.query<GetClassesResponse, { page: number }>({
      query: ({ page }) => ({
        url: 'classes',
        method: 'GET',
        params: { page },
        headers: {
          'X-Requested-Fields': StudentClassRequestedModelFields.join(','),
        },
      }),
    }),
    getStudentsByClass: builder.query<GetUsersByClassResponse, GetUsersByClassRequest>({
      query: ({ class_id, page, limit }) => ({
        url: `students`,
        method: 'GET',
        params: { class_id, page, limit },
        headers: {
          'X-Requested-Fields':
            'id,user_id,user:id,user:photo,user:color,user:photo_thumb,user:firstname,user:lastname,user:last_activity_date,user:is_online',
        },
      }),
    }),

    getTeachersByClass: builder.query<GetUsersByClassResponse, GetUsersByClassRequest>({
      query: ({ class_id, page, limit }) => ({
        url: `teachers`,
        method: 'GET',
        params: { class_id, page, limit },
        headers: {
          'X-Requested-Fields':
            'id,user_id,user:id,user:photo,user:color,user:photo_thumb,user:firstname,user:lastname,user:is_online',
        },
      }),
    }),
    getTeachersInClassAsManager: builder.query<
      GetStudentsInClassAsManagerResponse,
      GetStudentsInClassAsManagerParams
    >({
      query: ({ class_id, page = 1, limit = 10, sortBy = 'lastname' }) => ({
        url: `/teachers?class_id=${class_id}&page=${page}&limit=${limit}&sortBy=${sortBy}`,
        method: 'GET',
        headers: {
          'X-Requested-Fields': GetStudentsAsManagerFullInfoRequestedFields.join(','),
        },
      }),
      providesTags: (_r, _e, arg) => [{ type: "ClassMembers", id: `${arg.class_id}:teachers` }],
    }),
    getStudentsInClassAsManager: builder.query<
      GetStudentsInClassAsManagerResponse,
      GetStudentsInClassAsManagerParams
    >({
      query: ({ class_id, page = 1, limit = 10, sortBy = 'lastname', query, search }: any) => {
        const params = new URLSearchParams({
          class_id: class_id?.toString?.() ?? String(class_id),
          page: page.toString(),
          limit: limit.toString(),
          sortBy,
        });

        const q = (query ?? search)?.toString?.().trim?.() ?? '';
        if (q) params.append('query', q);

        return {
          url: `/students?${params.toString()}`,
          method: 'GET',
          headers: {
            'X-Requested-Fields': GetStudentsAsManagerFullInfoRequestedFields.join(','),
          },
        };
      },
      providesTags: (_r, _e, arg) => [{ type: "ClassMembers", id: `${arg.class_id}:students` }],
    }),
    getStudentsAsManager: builder.query<
      GetStudentsAsManagerResponse,
      GetStudentsAsManagerParams
    >({
      query: ({ page = 1, limit = 10, query }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });
        if (query) {
          params.append('query', query);
        }
        return {
          url: `/students?${params.toString()}`,
          method: 'GET',
          headers: {
            'X-Requested-Fields': GetStudentsAsManagerFullInfoRequestedFields.join(','),
          },
        };
      },
    }),
    getTeachersAsManager: builder.query<
      GetStudentsAsManagerResponse,
      GetStudentsAsManagerParams
    >({
      query: ({ page = 1, limit = 10, query }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });
        if (query) {
          params.append('query', query);
        }
        return {
          url: `/teachers?${params.toString()}`,
          method: 'GET',
          headers: {
            'X-Requested-Fields': GetStudentsAsManagerFullInfoRequestedFields.join(','),
          },
        };
      },
    }),

    // Получить все ID студентов группы (для формирования массива при исключениях)
    getAllStudentIdsInClass: builder.query<number[], { classId: number }>({
      query: ({ classId }) => ({
        url: `/students`,
        method: 'GET',
        params: {
          class_id: classId,
          page: 1,
          limit: 99999, // Получить всех студентов
        },
        headers: {
          'X-Requested-Fields': 'id', // Только ID для минимизации трафика
        },
      }),
      transformResponse: (response: GetStudentsInClassAsManagerResponse) => {
        const wrappers = response?.entities?.data ?? [];
        return wrappers.map((item: any) => item.id);
      },
      providesTags: (_r, _e, arg) => [{ type: "ClassMembers", id: `${arg.classId}:all-ids` }],
    }),

    // Удалить класс
    deleteClass: builder.mutation<void, number>({
      query: (classId) => ({
        url: `/classes/${classId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, classId) => [
        { type: 'ClassDetail', id: classId },
        'ClassesList',
      ],
    }),

    // Открепить студента от группы (каскадно из всех заданий этой группы)
    detachUserFromClass: builder.mutation<DetachUserFromClassResponse, { classId: number; studentId: number }>({
      query: ({ classId, studentId }) => ({
        url: `/classes/${classId}/detach-user.json`,
        method: 'POST',
        data: { student_id: studentId },
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: (_r, _e, { classId }) => [
        { type: 'ClassDetail', id: classId },
        { type: 'ClassMembers', id: `${classId}:students` },
        'ClassesList',
      ],
    }),
  }),
});

export const {
  useCreateClassMutation,
  useGetStudentsInAssignmentAsManagerQuery,
  useGetStudentInAssignmentAsManagerQuery,
  useLazyGetStudentInAssignmentAsManagerQuery,
  useGetTasksInClassAsManagerQuery,
  useGetStudentsByClassQuery,
  useGetTeachersByClassQuery,
  useGetTeachersInClassAsManagerQuery,
  useGetClassesQuery,
  useGetClassesAsManagerQuery,
  useUpdateClassMutation,
  useGetClassAsManagerQuery,
  useCreateBulkInClassMutation,
  useCreateBulkStudentsMutation,
  useSaveStudentsImportMockMutation,
  useGetStudentsInClassAsManagerQuery,
  useGetStudentsInAssignmentForProctoringQuery,
  useGetStudentsAsManagerQuery,
  useGetTeachersAsManagerQuery,
  useDeleteClassMutation,
  useDetachUserFromClassMutation,
  useGetExternalStudentsInAssignmentQuery,
  useGetAllStudentIdsInClassQuery,
  useLazyGetAllStudentIdsInClassQuery,
} = classApi;
